/**
 * Модальное окно настроек AI агента
 * Структурировано по табам: Модель, Router, База знаний
 */

import { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { useCustom } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Sparkles, Zap, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KnowledgeBaseSelector } from './knowledge-base-selector';

// Количество моделей для начальной загрузки и каждой подгрузки
const MODELS_PER_PAGE = 30;

interface ModelSummary {
  id: string;
  name: string;
  description: string | null;
  isFree: boolean;
  contextLength: number | null;
  maxOutputTokens: number | null;
}

interface ModelSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedModel: string | null;
  onSelect: (modelId: string | null) => void;
  // Router Agent settings
  routerEnabled: boolean;
  onRouterEnabledChange: (enabled: boolean) => void;
  routerModel: string | null;
  onRouterModelChange: (model: string | null) => void;
  // Web Search
  webSearchEnabled?: boolean;
  onWebSearchEnabledChange?: (enabled: boolean) => void;
  // Knowledge Base
  selectedDocuments?: number[];
  onSelectedDocumentsChange?: (ids: number[]) => void;
}

const formatTokens = (value: number | null | undefined) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : '—';

// Мемоизированный компонент элемента модели
const ModelItem = memo(function ModelItem({
  model,
  isSelected,
  onSelect,
}: {
  model: ModelSummary;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <CommandItem
      value={`${model.name} ${model.id}`}
      onSelect={onSelect}
      className="py-2"
    >
      <Check
        className={cn(
          'mr-2 h-4 w-4 flex-shrink-0',
          isSelected ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate">{model.name}</span>
          {model.isFree && (
            <Badge variant="secondary" className="text-[9px] px-1 py-0">
              free
            </Badge>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground truncate">
          {model.id}
        </span>
      </div>
    </CommandItem>
  );
});

export function ModelSelectorModal({
  open,
  onOpenChange,
  selectedModel,
  onSelect,
  routerEnabled,
  onRouterEnabledChange,
  routerModel,
  onRouterModelChange,
  selectedDocuments = [],
  onSelectedDocumentsChange,
}: ModelSelectorModalProps) {
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('model');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(MODELS_PER_PAGE);

  const modelsUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (showFreeOnly) {
      params.set('free', 'true');
    }
    params.set('sort', 'name:asc');
    return `/ai/models?${params.toString()}`;
  }, [showFreeOnly]);

  const customResult = useCustom<ModelSummary[]>({
    url: modelsUrl,
    method: 'get',
    queryOptions: {
      enabled: open,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  });

  const { data: modelsData, isLoading, isError, error } = customResult.query ?? {
    data: undefined,
    isLoading: false,
    isError: false,
    error: null,
  };

  const modelsRaw = modelsData?.data;
  const allModels = useMemo(() =>
    Array.isArray(modelsRaw) ? modelsRaw : [],
    [modelsRaw]
  );

  // Популярные модели для быстрого доступа
  const popularModelIds = useMemo(() => new Set([
    'openai/gpt-4o',
    'openai/gpt-4o-mini',
    'anthropic/claude-3.5-sonnet',
    'anthropic/claude-3-opus',
    'google/gemini-pro-1.5',
    'google/gemini-flash-1.5',
    'meta-llama/llama-3.1-405b-instruct',
    'meta-llama/llama-3.1-70b-instruct',
  ]), []);

  // Сортируем модели: популярные первыми
  const sortedModels = useMemo(() => {
    const popular: ModelSummary[] = [];
    const rest: ModelSummary[] = [];

    for (const model of allModels) {
      if (popularModelIds.has(model.id)) {
        popular.push(model);
      } else {
        rest.push(model);
      }
    }

    return [...popular, ...rest];
  }, [allModels, popularModelIds]);

  // Видимые модели с учётом поиска и пагинации
  const models = useMemo(() => {
    const hasSearch = searchQuery.trim().length > 0;
    if (hasSearch) {
      return sortedModels;
    }
    return sortedModels.slice(0, visibleCount);
  }, [sortedModels, searchQuery, visibleCount]);

  // Есть ли ещё модели для подгрузки
  const hasMore = useMemo(() => {
    if (searchQuery.trim().length > 0) {return false;}
    return visibleCount < sortedModels.length;
  }, [searchQuery, visibleCount, sortedModels.length]);

  // Сброс при изменении фильтра
  useEffect(() => {
    setVisibleCount(MODELS_PER_PAGE);
  }, [showFreeOnly]);

  // Обработчик скролла для подгрузки
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (scrollBottom < 100 && hasMore) {
      setVisibleCount(prev => prev + MODELS_PER_PAGE);
    }
  }, [hasMore]);

  if (open && isError) {
    console.error('Error loading models:', error);
  }

  const selectedModelData = useMemo(
    () => allModels.find(m => m.id === selectedModel),
    [allModels, selectedModel]
  );

  const routerModelData = useMemo(
    () => allModels.find(m => m.id === routerModel),
    [allModels, routerModel]
  );

  // При закрытии сбрасываем
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('');
      setVisibleCount(MODELS_PER_PAGE);
      setActiveTab('model');
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  // Выбор основной модели - закрывает модалку
  const handleSelectMainModel = useCallback((modelId: string) => {
    const resolvedModel = modelId === '__default__' ? null : modelId;
    onSelect(resolvedModel);
    handleOpenChange(false);
  }, [onSelect, handleOpenChange]);

  // Выбор Router модели - не закрывает модалку
  const handleSelectRouterModel = useCallback((modelId: string | null) => {
    onRouterModelChange(modelId);
  }, [onRouterModelChange]);

  // Компонент списка моделей (переиспользуемый)
  const renderModelList = (
    currentSelectedModel: string | null,
    currentModelData: ModelSummary | undefined,
    onSelectModel: (modelId: string) => void,
    defaultLabel: string,
    defaultDescription: string,
    listHeight: string = 'max-h-[280px]'
  ) => (
    <>
      {/* Выбранная модель */}
      {currentModelData ? (
        <div className="px-4 pb-3">
          <div className="rounded-lg p-3 space-y-1 bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{currentModelData.name}</span>
              {currentModelData.isFree && (
                <Badge variant="secondary" className="text-[10px]">free</Badge>
              )}
            </div>
            <div className="flex gap-4 text-[11px] text-muted-foreground">
              <span>Контекст: {formatTokens(currentModelData.contextLength)}</span>
              <span>Выход: {formatTokens(currentModelData.maxOutputTokens)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 pb-3">
          <div className="rounded-lg bg-muted/50 p-3">
            <span className="text-sm text-muted-foreground">
              {defaultLabel}
            </span>
          </div>
        </div>
      )}

      <Separator />

      {/* Фильтр */}
      <div className="px-4 py-2 flex items-center gap-2">
        <Switch
          id={`free-only-${activeTab}`}
          checked={showFreeOnly}
          onCheckedChange={setShowFreeOnly}
          className="scale-75"
        />
        <Label htmlFor={`free-only-${activeTab}`} className="text-xs text-muted-foreground cursor-pointer">
          Только бесплатные
        </Label>
      </div>

      {/* Список моделей */}
      <Command className="border-0" shouldFilter>
        <CommandInput
          placeholder="Поиск модели..."
          className="h-9"
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList className={listHeight} onScrollCapture={handleScroll}>
          {isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Загрузка моделей...
            </div>
          ) : (isError ? (
            <div className="py-6 text-center text-sm text-destructive">
              Ошибка загрузки моделей
            </div>
          ) : (
            <>
              <CommandEmpty>Модели не найдены</CommandEmpty>
              <CommandGroup>
                {/* Default option */}
                <CommandItem
                  value={`default ${defaultDescription}`}
                  onSelect={() => onSelectModel('__default__')}
                  className="py-2"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !currentSelectedModel ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">По умолчанию</span>
                    <span className="text-xs text-muted-foreground">
                      {defaultDescription}
                    </span>
                  </div>
                </CommandItem>

                <Separator className="my-1" />

                {/* Model list */}
                {models.map((model) => (
                  <ModelItem
                    key={model.id}
                    model={model}
                    isSelected={currentSelectedModel === model.id}
                    onSelect={() => onSelectModel(model.id)}
                  />
                ))}

                {/* Индикатор загрузки при скролле */}
                {hasMore && (
                  <div className="py-2 px-2 text-center text-[11px] text-muted-foreground">
                    Прокрутите вниз для загрузки ещё...
                  </div>
                )}
              </CommandGroup>
            </>
          ))}
        </CommandList>
      </Command>

      {/* Счётчик */}
      <div className="px-4 py-2 border-t text-[11px] text-muted-foreground">
        {isLoading ? 'Загрузка...' : `${allModels.length} моделей`}
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" />
            Настройки AI
          </DialogTitle>
        </DialogHeader>

        {/* Табы */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-4 pb-2">
            <TabsList className="w-full h-9">
              <TabsTrigger value="model" className="flex-1 text-xs gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                Модель
              </TabsTrigger>
              <TabsTrigger value="router" className="flex-1 text-xs gap-1.5">
                <Zap className="h-3.5 w-3.5" />
                Router
                {routerEnabled && (
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                )}
              </TabsTrigger>
              {onSelectedDocumentsChange && (
                <TabsTrigger value="knowledge" className="flex-1 text-xs gap-1.5">
                  <BookOpen className="h-3.5 w-3.5" />
                  База
                  {selectedDocuments.length > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 min-w-4">
                      {selectedDocuments.length}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Tab: Выбор модели */}
          <TabsContent value="model" forceMount className="mt-0 data-[state=inactive]:hidden">
            {renderModelList(
              selectedModel,
              selectedModelData,
              handleSelectMainModel,
              'Модель по умолчанию',
              'Использовать модель из настроек'
            )}
          </TabsContent>

          {/* Tab: Router Agent */}
          <TabsContent value="router" forceMount className="mt-0 data-[state=inactive]:hidden">
            {/* Описание и переключатель */}
            <div className="px-4 pb-3">
              <div className="rounded-lg bg-muted/50 p-3 space-y-3">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Двухэтапная архитектура: быстрая модель анализирует запрос и определяет,
                  какие tools нужны. Экономит токены и ускоряет ответы.
                </p>

                {/* Включить/выключить Router */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="router-enabled" className="text-xs cursor-pointer">
                    Использовать Router
                  </Label>
                  <Switch
                    id="router-enabled"
                    checked={routerEnabled}
                    onCheckedChange={onRouterEnabledChange}
                    className="scale-75"
                  />
                </div>
              </div>
            </div>

            {/* Выбор модели Router */}
            {routerEnabled && renderModelList(
              routerModel,
              routerModelData,
              (modelId) => handleSelectRouterModel(modelId === '__default__' ? null : modelId),
              'По умолчанию: gpt-4o-mini',
              'gpt-4o-mini (быстрая модель)',
              'max-h-[220px]'
            )}
          </TabsContent>

          {/* Tab: База знаний */}
          {onSelectedDocumentsChange && (
            <TabsContent value="knowledge" forceMount className="mt-0 px-4 pb-4 space-y-3 data-[state=inactive]:hidden">
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Выберите документы из базы знаний, которые AI будет использовать
                  для поиска информации (RAG). Релевантные фрагменты будут автоматически
                  добавлены в контекст запроса.
                </p>
              </div>

              <KnowledgeBaseSelector
                selectedDocuments={selectedDocuments}
                onSelectedDocumentsChange={onSelectedDocumentsChange}
              />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
